import React from 'react'

import {
  activeCellClass,
  Cell,
  EmptyState,
  hasStickyColumnStyle,
  Icon,
  ListLoading,
  Row,
} from './../../module'
import { getCustomColumnStickyStyles } from './Desktop/DesktopTable'
import { useFlattenNestedData } from './helperFunctions'
import type {
  ConfigItemType,
  NestedRowPropsType,
  StandardTableProps,
} from './StandardTable.models'
import styles from './_table.module.scss'
import { c } from '../../translations/LibraryTranslationService'

type NestedRowProps<
  DataItem,
  ConfigItem extends ConfigItemType<DataItem, Record<string, unknown>>,
  FilterOptions,
> = NestedRowPropsType<DataItem, ConfigItem> &
  Pick<StandardTableProps<DataItem, ConfigItem, FilterOptions>, 'hasCheckboxes'>

const NestedRow = <
  DataItem,
  ConfigItem extends ConfigItemType<DataItem, Record<string, unknown>>,
  FilterOptions,
>({
  data,
  nestedDataKey,
  nestedRowProps,
  hasCheckboxes,
  config,
  sortBy,
  customColumnProps,
  stickyTableConfig,
  selectedNestedRowData,
  totalRowKey,
  showStickyClasses,
  stickyRightCount,
}: NestedRowProps<DataItem, ConfigItem, FilterOptions>): React.JSX.Element => {
  // TODO: Figure out how to clean up all of these casting instances that were in this file
  const loading =
    nestedDataKey &&
    nestedRowProps?.nestedLoading?.[data?.[nestedDataKey] as unknown as string]

  const nestedData = useFlattenNestedData({
    nestedRowProps,
    nestedDataKey,
    data,
  })

  return (
    <>
      {nestedDataKey &&
      !loading &&
      (nestedRowProps?.nestedData[data?.[nestedDataKey] as unknown as string]
        ?.length as number) > 0 &&
      selectedNestedRowData &&
      selectedNestedRowData.length > 0 ? (
        selectedNestedRowData?.map((selectedRow, i) => {
          return (
            <React.Fragment key={i}>
              {selectedRow === data?.[nestedDataKey] &&
                (nestedRowProps?.nestedData?.[selectedRow as unknown as string]
                  ?.length as number) > 0 &&
                nestedData?.[selectedRow as unknown as string]?.map(
                  (nestedRow: DataItem, nestedRowIndex: number) => {
                    const nestedClassName =
                      nestedRowIndex === 0
                        ? styles.firstRowShadow
                        : nestedData?.[selectedRow as unknown as string]
                              .length -
                              1 ===
                            nestedRowIndex
                          ? styles.lastRowShadow
                          : ''

                    return (
                      <Row
                        key={`${selectedRow}-${nestedRowIndex}`}
                        className={`${styles.purpleBg} ${nestedClassName}`}
                      >
                        {hasCheckboxes && (
                          <Cell className={styles.checkboxWidth} />
                        )}
                        {config.map((c, configIndex) => {
                          const cells = []
                          if (c.options) {
                            c.options.forEach((co) => {
                              cells.push(co.name)
                            })
                          }
                          if (c.name) {
                            cells.push(c.name)
                          }
                          return (
                            <Cell
                              key={`${nestedRow[nestedDataKey]}-${c.name}`}
                              className={`${
                                sortBy
                                  ? activeCellClass({
                                      cells: cells,
                                      activeName: sortBy.prop,
                                    })
                                  : ''
                              } ${
                                (Number(stickyTableConfig?.left) || 1) ===
                                  configIndex + 1 && showStickyClasses
                                  ? styles.leftStickyCell
                                  : ''
                              } ${
                                Number(stickyTableConfig?.right) ===
                                  config.length - configIndex &&
                                showStickyClasses
                                  ? styles.rightStickyCell
                                  : ''
                              } ${hasStickyColumnStyle({
                                colIndex: configIndex + 1,
                                stickyLeftColumn: stickyTableConfig?.left,
                                stickyRightColumn: stickyTableConfig?.right,
                                tableHeadersLength: config.length,
                              })} ${
                                totalRowKey && data[totalRowKey]
                                  ? styles.lighterGrayBg
                                  : ''
                              } ${nestedClassName}`}
                            >
                              <div
                                className={
                                  configIndex === 0 ? styles.displayFlex : ''
                                }
                              >
                                {configIndex === 0 ? (
                                  <Icon
                                    icon='l'
                                    className={`${styles.childRowIcon} ${
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      nestedRow?.level > 0
                                        ? styles[
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            `padding${nestedRow.level * 16}`
                                          ]
                                        : ''
                                    }`}
                                    iconSize='8px'
                                  />
                                ) : null}
                                {c.cell.children(
                                  {
                                    ...nestedRow,
                                    sortProp: sortBy?.prop,
                                  },
                                  nestedRowIndex,
                                )}
                              </div>
                            </Cell>
                          )
                        })}
                        {nestedRowProps &&
                          nestedDataKey &&
                          !customColumnProps && (
                            <Cell
                              className={`${nestedClassName} ${
                                styles.actionItems
                              } ${getCustomColumnStickyStyles(
                                2,
                                stickyRightCount,
                                stickyTableConfig?.right,
                              )}}`}
                            />
                          )}
                        {customColumnProps && (
                          <Cell
                            className={`
                                ${
                                  totalRowKey && data[totalRowKey]
                                    ? styles.lighterGrayBg
                                    : ''
                                } ${nestedClassName} ${
                                  styles.actionItems
                                } ${getCustomColumnStickyStyles(
                                  1,
                                  stickyRightCount,
                                  stickyTableConfig?.right,
                                )}`}
                          />
                        )}
                      </Row>
                    )
                  },
                )}
            </React.Fragment>
          )
        })
      ) : loading ? (
        <Row key={`${nestedDataKey ? data?.[nestedDataKey] : ''}-loading`}>
          {hasCheckboxes && (
            <Cell
              className={`${styles.actionItems} ${styles.checkboxWidth}`}
              style={{ padding: '16px 0' }}
            >
              <ListLoading noSlideInUp style={{ borderRadius: '0' }} />
            </Cell>
          )}
          {config.map((c, index) => {
            return (
              <Cell
                key={`${c.name}-loading-cell`}
                style={{ padding: '16px 0' }}
              >
                <ListLoading
                  noSlideInUp
                  style={{ borderRadius: index === 0 ? '2px 0 0 2px' : '0' }}
                />
              </Cell>
            )
          })}
          {nestedRowProps && nestedDataKey && (
            <Cell className={styles.actionItems} style={{ padding: '16px 0' }}>
              <ListLoading noSlideInUp style={{ borderRadius: '0' }} />
            </Cell>
          )}
          {customColumnProps && (
            <Cell
              className={`${
                totalRowKey && data[totalRowKey] ? styles.lighterGrayBg : ''
              } ${styles.actionItems}`}
              style={{ padding: '16px 0' }}
            >
              <ListLoading
                noSlideInUp
                style={{ borderRadius: '0 2px 2px 0' }}
              />
            </Cell>
          )}
        </Row>
      ) : (
        nestedDataKey &&
        selectedNestedRowData?.includes(data[nestedDataKey]) &&
        nestedRowProps?.nestedData?.[data?.[nestedDataKey] as unknown as string]
          ?.length === 0 && (
          <Row key={`${nestedDataKey ? data?.[nestedDataKey] : ''}-no-data`}>
            {hasCheckboxes && <Cell className={styles.checkboxWidth} />}
            {config.map((co, index) => (
              <Cell key={`${co.name}-empty-state-cell`}>
                {index === 0 && (
                  <div className={styles.displayFlex}>
                    <Icon
                      icon='l'
                      className={styles.childRowIcon}
                      iconSize='8px'
                    />
                    <EmptyState primaryText={c('noDataAvailable')} />
                  </div>
                )}
              </Cell>
            ))}
            {nestedRowProps && nestedDataKey && <Cell />}
            {customColumnProps && <Cell />}
          </Row>
        )
      )}
    </>
  )
}

export default NestedRow
